import React, { useState, useEffect } from 'react'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Filter,
  MoreVertical,
  Calendar,
  Users,
  DollarSign,
  Globe,
  Settings
} from 'lucide-react'
import { huntAPI, sessionAPI, analyticsAPI, bookingAPI } from '../services/api'
import toast from 'react-hot-toast'

const Hunts = () => {
  const [hunts, setHunts] = useState([])
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedHunt, setSelectedHunt] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [bookingApis, setBookingApis] = useState([])
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    booking_api_name: '',
    platform_product_id: '',
    hunt_url: '',
    hunt_data: {
      basePrice: 25,
      currency: 'USD',
      estimatedDuration: '2-3 hours',
      difficulty: 'moderate',
      locations: []
    },
    mappings: []
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [huntsData, sessionsData, apisData] = await Promise.all([
        huntAPI.getAll(),
        sessionAPI.getAll(),
        bookingAPI.getAll()
      ])

      setHunts(huntsData.data || [])
      setSessions(sessionsData.data || [])
      setBookingApis(apisData.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateHunt = async () => {
    try {
      const payload = {
        ...formData,
        mappings: (formData.mappings || [])
          .filter(m => m.booking_api_name && m.platform_product_id)
          .map(m => ({
            booking_api_name: String(m.booking_api_name).toLowerCase(),
            platform_product_id: m.platform_product_id
          }))
      }
      const response = await huntAPI.create(payload)
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error creating hunt:', error)
    }
  }

  const handleUpdateHunt = async () => {
    try {
      const payload = {
        ...formData,
        mappings: (formData.mappings || [])
          .filter(m => m.booking_api_name && m.platform_product_id)
          .map(m => ({
            booking_api_name: String(m.booking_api_name).toLowerCase(),
            platform_product_id: m.platform_product_id
          }))
      }
      const response = await huntAPI.update(selectedHunt.id, payload)
      if (response.success) {
        setShowEditModal(false)
        resetForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error updating hunt:', error)
    }
  }

  const handleDeleteHunt = async () => {
    try {
      const response = await huntAPI.delete(selectedHunt.id)
      if (response.success) {
        setShowDeleteModal(false)
        fetchData()
      }
    } catch (error) {
      console.error('Error deleting hunt:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      booking_api_name: '',
      platform_product_id: '',
      hunt_url: '',
      hunt_data: {
        basePrice: 25,
        currency: 'USD',
        estimatedDuration: '2-3 hours',
        difficulty: 'moderate',
        locations: []
      },
      mappings: []
    })
  }

  const openEditModal = async (hunt) => {
    try {
      setSelectedHunt(hunt)
      const resp = await huntAPI.getById(hunt.id)
      const full = resp.data || hunt
      const resolvedMappings = Array.isArray(full.mappings) && full.mappings.length > 0
        ? full.mappings.map(m => ({ booking_api_name: m.booking_api_name, platform_product_id: m.platform_product_id }))
        : (full.booking_api_name && full.platform_product_id
            ? [{ booking_api_name: full.booking_api_name, platform_product_id: full.platform_product_id }]
            : [])
      setFormData({
        name: full.name,
        description: full.description || '',
        booking_api_name: full.booking_api_name,
        platform_product_id: full.platform_product_id,
        hunt_url: full.hunt_url || '',
        hunt_data: full.hunt_data || {
          basePrice: 25,
          currency: 'USD',
          estimatedDuration: '2-3 hours',
          difficulty: 'moderate',
          locations: []
        },
        mappings: resolvedMappings
      })
      setShowEditModal(true)
    } catch (e) {
      console.error('Error loading hunt details:', e)
      setFormData(prev => ({ ...prev, mappings: [] }))
      setShowEditModal(true)
    }
  }

  const openDeleteModal = (hunt) => {
    setSelectedHunt(hunt)
    setShowDeleteModal(true)
  }

  const getHuntStats = (huntId) => {
    const huntSessions = sessions.filter(s => s.hunt_id === huntId)
    const totalSessions = huntSessions.length
    const completedSessions = huntSessions.filter(s => s.status === 'completed').length
    const activeSessions = huntSessions.filter(s => s.status === 'active').length
    const expiredSessions = huntSessions.filter(s => s.status === 'expired').length

    return {
      total: totalSessions,
      completed: completedSessions,
      active: activeSessions,
      expired: expiredSessions
    }
  }

  const getBookingApiDisplayName = (apiName) => {
    const api = bookingApis.find(api => api.name === apiName)
    return api ? api.display_name : apiName
  }

  const filteredHunts = hunts.filter(hunt => {
    const matchesSearch = hunt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hunt.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hunt.platform_product_id?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && hunt.is_active) ||
                         (filterStatus === 'inactive' && !hunt.is_active)
    
    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Hunt Management</h1>
              <p className="text-gray-600 mt-1">Manage your scavenger hunt configurations and platform integrations</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 sm:mt-0 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Create New Hunt
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search hunts by name, description, or product ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Hunts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredHunts.map((hunt) => {
            const stats = getHuntStats(hunt.id)
            const apiDisplayName = getBookingApiDisplayName(hunt.booking_api_name)
            
            return (
              <div key={hunt.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* Hunt Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{hunt.name}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{hunt.description || 'No description'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        hunt.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {hunt.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <div className="relative">
                        <button
                          onClick={() => openEditModal(hunt)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Platform Info */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe size={16} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">{apiDisplayName}</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Product ID: {hunt.platform_product_id || 'Not set'}
                    </p>
                  </div>

                  {/* Hunt Data */}
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Base Price:</span>
                      <span className="font-medium">
                        {hunt.hunt_data?.basePrice || 25} {hunt.hunt_data?.currency || 'USD'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{hunt.hunt_data?.estimatedDuration || '2-3 hours'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Difficulty:</span>
                      <span className="font-medium capitalize">{hunt.hunt_data?.difficulty || 'moderate'}</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-blue-600">{stats.total}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-green-600">{stats.completed}</div>
                      <div className="text-xs text-gray-500">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-yellow-600">{stats.active}</div>
                      <div className="text-xs text-gray-500">Active</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-red-600">{stats.expired}</div>
                      <div className="text-xs text-gray-500">Expired</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(hunt)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button
                      onClick={() => openDeleteModal(hunt)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {filteredHunts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hunts found</h3>
            <p className="text-gray-600">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Create your first hunt to get started'
              }
            </p>
          </div>
        )}
      </div>

      {/* Create Hunt Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create New Hunt</h2>
              <p className="text-gray-600 mt-1">Configure a new scavenger hunt for any booking platform</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hunt Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Central Park Classic"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your scavenger hunt..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hunt URL</label>
                <input
                  type="url"
                  value={formData.hunt_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, hunt_url: e.target.value }))}
                  placeholder="https://yourdomain.com/hunt/central-park"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  The URL where users will access this hunt
                </p>
              </div>

              {/* Hunt Configuration */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Hunt Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Base Price</label>
                    <input
                      type="number"
                      value={formData.hunt_data.basePrice}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        hunt_data: { ...prev.hunt_data, basePrice: parseFloat(e.target.value) || 25 }
                      }))}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <select
                      value={formData.hunt_data.currency}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        hunt_data: { ...prev.hunt_data, currency: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                    <input
                      type="text"
                      value={formData.hunt_data.estimatedDuration}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        hunt_data: { ...prev.hunt_data, estimatedDuration: e.target.value }
                      }))}
                      placeholder="2-3 hours"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                  <select
                    value={formData.hunt_data.difficulty}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hunt_data: { ...prev.hunt_data, difficulty: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              {/* Platform Mappings */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Mappings</h3>
                <div className="space-y-3">
                  {(formData.mappings || []).map((m, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                        <select
                          value={m.booking_api_name}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            mappings: prev.mappings.map((row, i) => i === idx ? { ...row, booking_api_name: e.target.value } : row)
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Platform</option>
                          {bookingApis.map(api => (
                            <option key={api.name} value={api.name}>{api.display_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Platform Product ID</label>
                        <input
                          type="text"
                          value={m.platform_product_id}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            mappings: prev.mappings.map((row, i) => i === idx ? { ...row, platform_product_id: e.target.value } : row)
                          }))}
                          placeholder="product_12345"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            mappings: prev.mappings.filter((_, i) => i !== idx)
                          }))}
                          className="w-full px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        mappings: [...(prev.mappings || []), { booking_api_name: '', platform_product_id: '' }]
                      }))}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-sm"
                    >
                      + Add Mapping
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Add one or more mappings. The first mapping will be used as default display and for initial platform fields.</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Revenue Configuration</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      This price will be used for revenue calculations and analytics. Commission rates from your configured booking APIs will be automatically applied.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateHunt}
                disabled={!formData.name}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
              >
                Create Hunt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Hunt Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Edit Hunt</h2>
              <p className="text-gray-600 mt-1">Update hunt configuration and settings</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hunt Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Central Park Classic"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your scavenger hunt..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hunt URL</label>
                <input
                  type="url"
                  value={formData.hunt_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, hunt_url: e.target.value }))}
                  placeholder="https://yourdomain.com/hunt/central-park"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  The URL where users will access this hunt
                </p>
              </div>

              {/* Hunt Configuration */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Hunt Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Base Price</label>
                    <input
                      type="number"
                      value={formData.hunt_data.basePrice}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        hunt_data: { ...prev.hunt_data, basePrice: parseFloat(e.target.value) || 25 }
                      }))}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <select
                      value={formData.hunt_data.currency}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        hunt_data: { ...prev.hunt_data, currency: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                    <input
                      type="text"
                      value={formData.hunt_data.estimatedDuration}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        hunt_data: { ...prev.hunt_data, estimatedDuration: e.target.value }
                      }))}
                      placeholder="2-3 hours"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                  <select
                    value={formData.hunt_data.difficulty}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hunt_data: { ...prev.hunt_data, difficulty: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              {/* Platform Mappings */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Mappings</h3>
                <div className="space-y-3">
                  {(formData.mappings || []).map((m, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                        <select
                          value={m.booking_api_name}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            mappings: prev.mappings.map((row, i) => i === idx ? { ...row, booking_api_name: e.target.value } : row)
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Platform</option>
                          {bookingApis.map(api => (
                            <option key={api.name} value={api.name}>{api.display_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Platform Product ID</label>
                        <input
                          type="text"
                          value={m.platform_product_id}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            mappings: prev.mappings.map((row, i) => i === idx ? { ...row, platform_product_id: e.target.value } : row)
                          }))}
                          placeholder="product_12345"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            mappings: prev.mappings.filter((_, i) => i !== idx)
                          }))}
                          className="w-full px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        mappings: [...(prev.mappings || []), { booking_api_name: '', platform_product_id: '' }]
                      }))}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-sm"
                    >
                      + Add Mapping
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Add one or more mappings. The first mapping will be used as default display and for initial platform fields.</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Revenue Configuration</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      This price will be used for revenue calculations and analytics. Commission rates from your configured booking APIs will be automatically applied.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => { setShowEditModal(false); setSelectedHunt(null); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateHunt}
                disabled={!formData.name}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
              >
                Update Hunt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Hunt</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone.</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>{selectedHunt?.name}</strong>? 
                This will permanently remove the hunt and all associated data.
              </p>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteHunt}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Delete Hunt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Hunts
